/**
 * BrainPopover — the diagnosis panel as a floating popover (plan 15.4, stages F2/F3).
 *
 * FloatingPanel (F1) with BRAIN_PANEL_PRESET, controlled by popoverReducer (popoverLogic.js), holding the
 * vendored POC panel (useVendoredPanel + BrainPanelHost). Fed by tse:diag_state through useBrainState;
 * panel clicks go out as tse:action commands via onCommand(action, params); the Questions tab's pending
 * row calls onFocusComposer. Hidden on the enterprise variant (O9, 16.1).
 *
 * Props: diagState, sessionId, username, enterprise, overlayOpen, devMode, fetcher (the app's authFetch),
 * onCommand, onFocusComposer, onOpenEditor (the Teach card's "Open learning editor"), openRequest ({tab, section, nonce}: open on a
 * tab, from the learning banner),
 * onExit (leave differential diagnosis: the same tse:exit_guided as the chat's Exit bar; a header chip while the diagnosis is open).
 */
import React, { useCallback, useEffect, useReducer, useRef } from "react";

import { configureBrainApi, vendoredPanel } from "../../vendor/brain";
import BrainPanelHost from "./BrainPanelHost";
import { commandFromPanel } from "./brainCommands";
import FloatingPanel from "./FloatingPanel";
import { BRAIN_PANEL_PRESET, PANEL_STATES } from "./floatingPanelConstants";
import { brainStorageKey, loadPanelPrefs, savePanelPrefs } from "./panelPersistence";
import { diagnosisOpen, initialPopover, POPOVER_ACTIONS, popoverReducer } from "./popoverLogic";
import useCategoryBundle, { learningKeyOf } from "./useCategoryBundle";
import useVendoredPanel from "./useVendoredPanel";
import LearningSwitch from "../dashboard5/LearningSwitch";
import "./brainPopover.css";

export const BRAIN_POPOVER_TITLE = "Diagnosis";
const PANEL_ID = "d5-brain-popover";

const panelClassName = (shown) => `${BRAIN_PANEL_PRESET.className} d5-brain-frame--${shown}`;
export const EXIT_LABEL = "Exit differential diagnosis";

/** Compact Exit chip in the popover header (same look as the composer's .d5-exit-guided chip). */
function ExitChip({ onExit }) {
  return (
    <button type="button" className="d5-brain-exit" aria-label={EXIT_LABEL} title={EXIT_LABEL} onClick={onExit}>
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
        strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
      </svg>
      <span>{EXIT_LABEL}</span>
    </button>
  );
}

function BrainPopoverInner({
  diagState = null,
  sessionId = "",
  username = "",
  overlayOpen = false,
  devMode = false,
  fetcher = null,
  onCommand,
  onFocusComposer,
  onOpenEditor,
  openRequest = null,
  onExit,
  learning = null,
}) {
  const persistKey = brainStorageKey(username);
  const [pop, dispatch] = useReducer(popoverReducer, initialPopover);
  const prevStateRef = useRef(null);

  useEffect(() => {
    configureBrainApi({ fetcher, sessionId });
  }, [fetcher, sessionId]);

  // New session or clear: hidden, nothing of the previous diagnosis readable (15.4).
  useEffect(() => {
    dispatch({ type: POPOVER_ACTIONS.RESET });
    prevStateRef.current = null;
  }, [sessionId]);

  useEffect(() => {
    if (!diagState) {
      // Cleared under the same session id: hide and forget, like a new session.
      if (prevStateRef.current) dispatch({ type: POPOVER_ACTIONS.RESET });
      prevStateRef.current = null;
      return;
    }
    const prefs = loadPanelPrefs(persistKey);
    dispatch({
      type: POPOVER_ACTIONS.DIAG_STATE, prev: prevStateRef.current, next: diagState,
      preferBubble: prefs.state === PANEL_STATES.BUBBLE,
    });
    prevStateRef.current = diagState;
  }, [diagState, persistKey]);

  useEffect(() => {
    dispatch({ type: POPOVER_ACTIONS.OVERLAY, open: !!overlayOpen });
  }, [overlayOpen]);

  const bundle = useCategoryBundle(sessionId, diagState?.diagnosis_id, diagState?.category, learningKeyOf(diagState));
  const focusedLod = diagState?.focused_lod || null;

  const onPanelCallback = useCallback((name, args) => {
    if (name === "onTabChange") { savePanelPrefs(persistKey, { tab: args[0] }); return; }
    if (name === "onPendingQuestion") { onFocusComposer?.(args[0] || null); return; }
    if (name === "onOpenEditor") { onOpenEditor?.({ categoryLabel: bundle?.label || null }); return; }
    const cmd = commandFromPanel(name, args, { focusedLod });
    if (cmd) onCommand?.(cmd.action, cmd.params);
  }, [persistKey, onFocusComposer, onOpenEditor, onCommand, focusedLod]);

  const { hostEl, focusTab } = useVendoredPanel({
    diagState, bundle, devMode, initialTab: loadPanelPrefs(persistKey).tab, onPanelCallback,
  });

  const lastNonceRef = useRef(null);
  useEffect(() => {
    if (!openRequest || openRequest.nonce === lastNonceRef.current) return;
    lastNonceRef.current = openRequest.nonce;
    dispatch({ type: POPOVER_ACTIONS.OPEN });
    if (openRequest.tab) focusTab(openRequest.tab, openRequest.section);
  }, [openRequest, focusTab]);

  const onPanelStateChange = useCallback((next) => {
    dispatch({ type: POPOVER_ACTIONS.PANEL, next });
  }, []);

  const onResize = useCallback(() => {
    const { DiagMap } = vendoredPanel();
    if (DiagMap && typeof DiagMap.relayout === "function") DiagMap.relayout();
  }, []);

  return (
    <FloatingPanel
      {...BRAIN_PANEL_PRESET}
      id={PANEL_ID}
      className={panelClassName}
      title={BRAIN_POPOVER_TITLE}
      panelState={pop.panel}
      onPanelStateChange={onPanelStateChange}
      persistKey={persistKey}
      bubbleCount={pop.updates}
      pulse={pop.pulse}
      onResize={onResize}
      frameProps={{ "data-diagnosis-id": diagState?.diagnosis_id || "", "data-seq": diagState?.seq || "" }}
      headerActions={onExit && diagnosisOpen(diagState) ? (
        <>
          {learning && <LearningSwitch {...learning} />}
          <ExitChip onExit={onExit} />
        </>
      ) : null}
    >
      <BrainPanelHost hostEl={hostEl} />
    </FloatingPanel>
  );
}

/** Enterprise variant: nothing is mounted at all (no vendored panel, no fetches). */
export default function BrainPopover(props) {
  if (props.enterprise) return null;
  return <BrainPopoverInner {...props} />;
}
